import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Button,
  Badge,
  Input,
  Dropdown,
  Option,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Search24Regular,
  Document24Regular,
  DocumentText24Regular,
  Receipt24Regular,
  ArrowDownload24Regular,
  Eye24Regular,
} from '@fluentui/react-icons';
import DataTable, { ColumnConfig } from '../../components/DataTable';
import { PortalDocument } from '../types';
import { fetchDocumentPdf } from '../services/portalApi';
import { formatCalendarDate } from '../../lib/businessTime';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  filters: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
  },
  searchBox: {
    width: '300px',
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    ...shorthands.overflow('hidden'),
  },
  documentType: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  documentIcon: {
    width: '36px',
    height: '36px',
    ...shorthands.borderRadius('8px'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  documentIconInvoice: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
  },
  documentIconQuote: {
    backgroundColor: tokens.colorPaletteBerryBackground2,
    color: tokens.colorPaletteBerryForeground2,
  },
  documentIconContract: {
    backgroundColor: tokens.colorPaletteNavyBackground2,
    color: tokens.colorPaletteNavyForeground2,
  },
  documentInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  documentNumber: {
    fontWeight: '600',
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  documentOrder: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  amount: {
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  actions: {
    display: 'flex',
    gap: '4px',
  },
  emptyState: {
    textAlign: 'center' as const,
    ...shorthands.padding('48px'),
    color: tokens.colorNeutralForeground3,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding('48px'),
  },
  summaryCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  summaryCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    ...shorthands.padding('20px'),
  },
  summaryLabel: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    marginBottom: '8px',
  },
  summaryValue: {
    fontSize: '24px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  summaryValueDue: {
    color: tokens.colorPaletteRedForeground1,
  },
});

// Mock data
const mockDocuments: PortalDocument[] = [
  {
    id: 'doc-1',
    number: 'INV-1234',
    type: 'invoice',
    status: 'sent',
    date: '2025-12-10',
    dueDate: '2025-12-25',
    amount: 432,
    paid: 432,
    balance: 0,
    paymentStatus: 'paid',
    orderId: 'order-1',
    orderNumber: '1234',
  },
  {
    id: 'doc-2',
    number: 'INV-1220',
    type: 'invoice',
    status: 'sent',
    date: '2025-11-22',
    dueDate: '2025-12-07',
    amount: 350,
    paid: 0,
    balance: 350,
    paymentStatus: 'overdue',
    orderId: 'order-3',
    orderNumber: '1201',
  },
  {
    id: 'doc-3',
    number: 'INV-1215',
    type: 'invoice',
    status: 'sent',
    date: '2025-11-15',
    dueDate: '2025-11-30',
    amount: 486,
    paid: 486,
    balance: 0,
    paymentStatus: 'paid',
    orderId: 'order-3',
    orderNumber: '1201',
  },
  {
    id: 'doc-4',
    number: 'QT-1240',
    type: 'quote',
    status: 'sent',
    date: '2025-12-05',
    amount: 500,
    paid: 0,
    balance: 500,
    paymentStatus: 'payment_due',
    orderId: 'order-new',
    orderNumber: '1240',
  },
  {
    id: 'doc-5',
    number: 'CT-1234',
    type: 'contract',
    status: 'signed',
    date: '2025-12-09',
    amount: 0,
    paid: 0,
    balance: 0,
    paymentStatus: 'paid',
    orderId: 'order-1',
    orderNumber: '1234',
  },
];

const Documents: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<PortalDocument[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownloadPdf = async (doc: PortalDocument) => {
    setDownloadingId(doc.id);
    try {
      const blob = await fetchDocumentPdf(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.type.charAt(0).toUpperCase() + doc.type.slice(1)} ${doc.number || doc.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed', err);
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    const loadDocuments = async () => {
      setLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        setDocuments(mockDocuments);
      } finally {
        setLoading(false);
      }
    };
    loadDocuments();
  }, []);

  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <Badge appearance="filled" color="success">Paid</Badge>;
      case 'payment_due':
        return <Badge appearance="filled" color="warning">Due</Badge>;
      case 'overdue':
        return <Badge appearance="filled" color="danger">Overdue</Badge>;
      case 'partial':
        return <Badge appearance="filled" color="warning">Partial</Badge>;
      default:
        return <Badge appearance="outline">{status}</Badge>;
    }
  };

  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'invoice':
        return (
          <div className={`${styles.documentIcon} ${styles.documentIconInvoice}`}>
            <Receipt24Regular />
          </div>
        );
      case 'quote':
        return (
          <div className={`${styles.documentIcon} ${styles.documentIconQuote}`}>
            <DocumentText24Regular />
          </div>
        );
      case 'contract':
        return (
          <div className={`${styles.documentIcon} ${styles.documentIconContract}`}>
            <Document24Regular />
          </div>
        );
      default:
        return (
          <div className={styles.documentIcon}>
            <Document24Regular />
          </div>
        );
    }
  };

  const formatDate = (dateString: string) => formatCalendarDate(dateString);

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = searchQuery === '' ||
      doc.number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.orderNumber && doc.orderNumber.includes(searchQuery));

    const matchesType = typeFilter === 'all' || doc.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || doc.paymentStatus === statusFilter;

    return matchesSearch && matchesType && matchesStatus;
  });

  // Calculate summary
  const totalOutstanding = documents
    .filter(d => d.type === 'invoice' && d.balance > 0)
    .reduce((sum, d) => sum + d.balance, 0);

  const overdueAmount = documents
    .filter(d => d.paymentStatus === 'overdue')
    .reduce((sum, d) => sum + d.balance, 0);

  const columns: ColumnConfig<PortalDocument>[] = [
    {
      columnKey: 'number',
      label: 'Document',
      width: '280px',
      render: (doc) => (
        <div className={styles.documentType}>
          {getDocumentIcon(doc.type)}
          <div className={styles.documentInfo}>
            <Text className={styles.documentNumber}>
              {doc.number}
            </Text>
            {doc.orderNumber && (
              <Text className={styles.documentOrder}>Order #{doc.orderNumber}</Text>
            )}
          </div>
        </div>
      ),
    },
    {
      columnKey: 'type',
      label: 'Type',
      width: '100px',
      render: (doc) => (
        <Text style={{ textTransform: 'capitalize' }}>{doc.type}</Text>
      ),
    },
    {
      columnKey: 'date',
      label: 'Date',
      width: '120px',
      render: (doc) => formatDate(doc.date),
    },
    {
      columnKey: 'amount',
      label: 'Amount',
      width: '120px',
      render: (doc) => (
        <Text className={styles.amount}>
          {doc.amount > 0 ? `$${doc.amount.toFixed(2)}` : '-'}
        </Text>
      ),
    },
    {
      columnKey: 'status',
      label: 'Status',
      width: '100px',
      render: (doc) => doc.type === 'invoice' ? getPaymentStatusBadge(doc.paymentStatus) : (
        <Badge appearance="outline">{doc.status}</Badge>
      ),
    },
    {
      columnKey: 'id',
      label: '',
      width: '150px',
      render: (doc) => (
        <div className={styles.actions}>
          <Button
            appearance="subtle"
            size="small"
            icon={<Eye24Regular />}
            title="View"
          />
          <Button
            appearance="subtle"
            size="small"
            icon={<ArrowDownload24Regular />}
            title="Download PDF"
            disabled={downloadingId === doc.id || doc.type === 'contract'}
            onClick={() => handleDownloadPdf(doc)}
          />
          {doc.type === 'invoice' && doc.balance > 0 && (
            <Button
              appearance="primary"
              size="small"
            >
              Pay
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading documents..." />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className={styles.header}>
        <Text as="h1" className={styles.title}>Documents</Text>
      </div>

      {/* Summary Cards */}
      <div className={styles.summaryCards}>
        <div className={styles.summaryCard}>
          <Text className={styles.summaryLabel}>Total Documents</Text>
          <Text className={styles.summaryValue}>{documents.length}</Text>
        </div>
        <div className={styles.summaryCard}>
          <Text className={styles.summaryLabel}>Outstanding Balance</Text>
          <Text className={`${styles.summaryValue} ${totalOutstanding > 0 ? styles.summaryValueDue : ''}`}>
            ${totalOutstanding.toFixed(2)}
          </Text>
        </div>
        <div className={styles.summaryCard}>
          <Text className={styles.summaryLabel}>Overdue</Text>
          <Text className={`${styles.summaryValue} ${overdueAmount > 0 ? styles.summaryValueDue : ''}`}>
            ${overdueAmount.toFixed(2)}
          </Text>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <Input
          className={styles.searchBox}
          contentBefore={<Search24Regular />}
          placeholder="Search documents..."
          value={searchQuery}
          onChange={(e, data) => setSearchQuery(data.value)}
        />
        <Dropdown
          placeholder="Type"
          value={typeFilter === 'all' ? 'All Types' : typeFilter}
          onOptionSelect={(e, data) => setTypeFilter(data.optionValue || 'all')}
        >
          <Option value="all">All Types</Option>
          <Option value="invoice">Invoice</Option>
          <Option value="quote">Quote</Option>
          <Option value="contract">Contract</Option>
        </Dropdown>
        <Dropdown
          placeholder="Status"
          value={statusFilter === 'all' ? 'All Statuses' : statusFilter}
          onOptionSelect={(e, data) => setStatusFilter(data.optionValue || 'all')}
        >
          <Option value="all">All Statuses</Option>
          <Option value="paid">Paid</Option>
          <Option value="payment_due">Due</Option>
          <Option value="overdue">Overdue</Option>
        </Dropdown>
      </div>

      {/* Documents Table */}
      <div className={styles.card}>
        {filteredDocuments.length === 0 ? (
          <div className={styles.emptyState}>
            <Document24Regular style={{ fontSize: '48px', marginBottom: '16px' }} />
            <Text style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              No documents found
            </Text>
            <Text>
              {searchQuery || typeFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'You don\'t have any documents yet'}
            </Text>
          </div>
        ) : (
          <DataTable
            data={filteredDocuments}
            columns={columns}
            keyField="id"
            hideableColumns
            columnVisibilityStorageKey="portal_documents"
            itemsPerPage={15}
          />
        )}
      </div>
    </div>
  );
};

export default Documents;
